import React, { useState } from "react";
import { useEffect } from "react";
import { auditApi } from "../../services/api";

interface Props {
  resource: string; // e.g., 'variables'
  id: number;
  fetch: (
    resource: string,
    id: number,
  ) => Promise<import("../../types/audit").AuditEntry | null>;
}

const AuditInfo: React.FC<Props> = ({ resource, id, fetch }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [entry, setEntry] = useState<
    import("../../types/audit").AuditEntry | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [beforeNow, setBeforeNow] = useState<
    import("../../types/audit").AuditEntry[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(resource, id);
        if (!cancelled) setEntry(res);
        // Also load the last two entries for Before/Now diffs
        try {
          const two = await auditApi.getLastTwo(resource, id);
          if (!cancelled) setBeforeNow(two || []);
        } catch {}
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resource, id, fetch]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
  };

  const nowEntry = beforeNow[0] || null; // newest
  const beforeEntry = beforeNow[1] || null; // previous

  const parseJson = (s?: string | null) => {
    if (!s) return null;
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  };

  const tryParseNestedJson = (value: unknown) => {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (
        (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))
      ) {
        try {
          return JSON.parse(trimmed);
        } catch {
          return value;
        }
      }
    }
    return value;
  };

  const extractBodyRaw = (
    e: import("../../types/audit").AuditEntry | null,
  ): unknown => {
    if (!e) return "";
    const obj = parseJson(e.details);
    const bodyVal =
      obj && typeof obj === "object" && (obj as any).body !== undefined
        ? (obj as any).body
        : e.details;
    // Attempt to parse nested JSON if body was a JSON string
    return tryParseNestedJson(bodyVal);
  };

  const areValuesEqual = (a: unknown, b: unknown): boolean => {
    if (a === b) return true;
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return String(a) === String(b);
    }
  };

  const formatValue = (val: unknown): string => {
    if (val === null || val === undefined) return "" + val;
    if (typeof val === "string") return val;
    if (typeof val === "number" || typeof val === "boolean") return String(val);
    if (val instanceof Date) return val.toISOString();
    try {
      const str = JSON.stringify(val);
      return str.length > 200 ? str.slice(0, 200) + "…" : str;
    } catch {
      return String(val);
    }
  };

  const renderKeyValueList = (
    data: unknown,
    diffAgainst?: unknown,
    variant: "before" | "now" = "now",
  ) => {
    const changedClass = variant === "before" ? "bg-red-200" : "bg-green-200";
    // Support plain strings by showing a single list item
    if (
      typeof data === "string" ||
      typeof data === "number" ||
      typeof data === "boolean" ||
      data == null
    ) {
      const changed =
        diffAgainst !== undefined && !areValuesEqual(data, diffAgainst);
      return (
        <ul className="mt-1 bg-gray-50 p-2 rounded overflow-auto text-[11px] space-y-1">
          <li className={changed ? `${changedClass} rounded px-1` : undefined}>
            <span className="text-gray-500">value:</span>{" "}
            <span className="font-mono break-all">{formatValue(data)}</span>
          </li>
        </ul>
      );
    }

    // Arrays: show index: value
    if (Array.isArray(data)) {
      const diffArray = Array.isArray(diffAgainst) ? diffAgainst : [];
      return (
        <ul className="mt-1 bg-gray-50 p-2 rounded overflow-auto text-[11px] space-y-1">
          {data.map((item, index) => {
            const changed =
              diffAgainst !== undefined &&
              !areValuesEqual(item, (diffArray as any)[index]);
            return (
              <li
                key={index}
                className={changed ? `${changedClass} rounded px-1` : undefined}
              >
                <span className="text-gray-500">[{index}]</span>{" "}
                <span className="font-mono break-all">{formatValue(item)}</span>
              </li>
            );
          })}
        </ul>
      );
    }

    // Objects: list own enumerable keys
    if (typeof data === "object") {
      const entries = Object.entries(data as Record<string, unknown>);
      const diffObj =
        diffAgainst &&
        typeof diffAgainst === "object" &&
        !Array.isArray(diffAgainst)
          ? (diffAgainst as Record<string, unknown>)
          : undefined;
      return (
        <ul className="mt-1 bg-gray-50 p-2 rounded overflow-auto text-[11px] space-y-1">
          {entries.length === 0 ? (
            <li className="text-gray-400">(empty)</li>
          ) : (
            entries.map(([key, val]) => {
              const beforeVal = diffObj ? (diffObj as any)[key] : undefined;
              const changed =
                diffObj !== undefined && !areValuesEqual(val, beforeVal);
              return (
                <li
                  key={key}
                  className={
                    changed ? `${changedClass} rounded px-1` : undefined
                  }
                >
                  <span className="text-gray-500">{key}:</span>{" "}
                  <span className="font-mono break-all">
                    {formatValue(val)}
                  </span>
                </li>
              );
            })
          )}
        </ul>
      );
    }

    // Fallback
    return (
      <ul className="mt-1 bg-gray-50 p-2 rounded overflow-auto text-[11px] space-y-1">
        <li>
          <span className="text-gray-500">value:</span>{" "}
          <span className="font-mono break-all">
            {formatValue(String(data))}
          </span>
        </li>
      </ul>
    );
  };

  return (
    <div className="fixed bottom-4 right-4 z-30">
      <div className="flex flex-col items-end gap-2">
        {open && (
          <div className="w-96 max-w-[90vw] bg-white shadow-lg border border-gray-200 rounded-md p-3 text-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-gray-500">Last modification</div>
              <button
                onClick={toggle}
                className="text-gray-400 hover:text-gray-600 text-xs"
                aria-label="Close audit panel"
              >
                ✕
              </button>
            </div>
            {loading ? (
              <div className="text-gray-500 text-xs">Loading…</div>
            ) : error ? (
              <div className="text-red-600 text-xs">{error}</div>
            ) : entry ? (
              <div className="space-y-2">
                <div>
                  <span className="text-gray-500">User:</span>{" "}
                  <span className="font-medium">{entry.user_email ?? "-"}</span>
                </div>
                <div>
                  <span className="text-gray-500">When:</span>{" "}
                  <span className="font-medium">
                    {new Date(entry.timestamp).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Action:</span>{" "}
                  <span className="font-medium">
                    {entry.description || "-"}
                  </span>{" "}
                  <span className="text-gray-400">({entry.action})</span>
                </div>

                {(beforeEntry || nowEntry) && (
                  <div className="mt-2 grid grid-cols-1 gap-2">
                    {beforeEntry ? (
                      <div>
                        <div className="text-xs font-medium text-gray-600">
                          Before
                        </div>
                        {renderKeyValueList(
                          extractBodyRaw(beforeEntry),
                          extractBodyRaw(nowEntry),
                          "before",
                        )}
                      </div>
                    ) : null}
                    {nowEntry ? (
                      <div>
                        <div className="text-xs font-medium text-gray-600">
                          Now
                        </div>
                        {renderKeyValueList(
                          extractBodyRaw(nowEntry),
                          extractBodyRaw(beforeEntry),
                          "now",
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-500 text-xs">No audit info</div>
            )}
          </div>
        )}
        <button
          onClick={toggle}
          className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs hover:bg-gray-200 border border-gray-200"
          aria-expanded={open}
        >
          {open ? "Hide last change" : "Last change"}
        </button>
      </div>
    </div>
  );
};

export default AuditInfo;
